/**
 * Header Module
 *
 * Handles header scroll behavior (background change, hide/show).
 *
 * @package TheRealEstates
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

export default class Header {
  constructor() {
    this.header = document.querySelector('[data-header]');

    if (!this.header) return;

    this.isScrolled = false;
    this.isHidden = false;
    this.lastScrollY = 0;
    this.scrollThreshold = 100;
    this.hideThreshold = 300;

    this.init();
  }

  /**
   * Initialize header behaviors
   */
  init() {
    this.setupScrollState();
    this.setupHideOnScroll();
  }

  /**
   * Add scrolled class when past threshold
   */
  setupScrollState() {
    ScrollTrigger.create({
      start: `top -${this.scrollThreshold}`,
      onUpdate: (self) => {
        const shouldBeScrolled = self.progress > 0;

        if (shouldBeScrolled && !this.isScrolled) {
          this.header.classList.add('is-scrolled');
          this.isScrolled = true;
        } else if (!shouldBeScrolled && this.isScrolled) {
          this.header.classList.remove('is-scrolled');
          this.isScrolled = false;
        }
      },
    });
  }

  /**
   * Hide header on scroll down, show on scroll up
   */
  setupHideOnScroll() {
    let lastScrollY = 0;
    let ticking = false;

    const updateHeader = () => {
      const scrollY = window.scrollY || document.documentElement.scrollTop;
      const direction = scrollY > lastScrollY ? 'down' : 'up';
      const delta = Math.abs(scrollY - lastScrollY);

      // Only respond to significant scroll
      if (delta < 10) {
        ticking = false;
        return;
      }

      // Only hide after threshold
      if (scrollY > this.hideThreshold) {
        if (direction === 'down' && !this.isHidden) {
          this.hide();
        } else if (direction === 'up' && this.isHidden) {
          this.show();
        }
      } else if (this.isHidden) {
        this.show();
      }

      lastScrollY = scrollY;
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(updateHeader);
          ticking = true;
        }
      },
      { passive: true }
    );
  }

  /**
   * Hide the header
   */
  hide() {
    this.isHidden = true;
    gsap.to(this.header, {
      yPercent: -100,
      duration: 0.3,
      ease: 'power2.inOut',
      onComplete: () => {
        this.header.classList.add('is-hidden');
      },
    });
  }

  /**
   * Show the header
   */
  show() {
    this.isHidden = false;
    this.header.classList.remove('is-hidden');
    gsap.to(this.header, {
      yPercent: 0,
      duration: 0.3,
      ease: 'power2.inOut',
    });
  }

  /**
   * Destroy ScrollTriggers
   */
  destroy() {
    ScrollTrigger.getAll()
      .filter((st) => st.vars.trigger === this.header)
      .forEach((st) => st.kill());
  }
}
